import { useCallback, useState } from "react"
import { useField } from "formik"

import { ErrorDisplay } from "../../../../../../core/api/errorTypes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import type { MeetingServicePriceListState } from "../../../createOrderTypes"
import { useCreateOrderDialogType } from "../../../hooks/useCreateOrderDialogType"
import type { DeliveryTimesField } from "../../form/formTypes"

import {
    DeadlineMessageDisplayType,
    DeadlineMessageProductFlow,
    fetchDeadlineMessage,
    selectDeadlineMessage,
} from "./deliveryApi"
import type { DeadlineMessageInfo } from "./deliveryApi"
import { buildDeadlineMessageParams } from "./helpers"
import { useDeadlineSnackbar } from "./useDeadlineSnackbar"

// a mutation, not a query: the warning belongs to one click and must never come from cache
export function useAddedProductDeadlineWarning() {
    const dateTimeFactory = useDateTime()
    const { isSpecialOrder } = useCreateOrderDialogType()
    const showDeadlineSnackbar = useDeadlineSnackbar()
    const [deadlineDialogMessage, setDeadlineDialogMessage] = useState<DeadlineMessageInfo | null>(null)

    const [{ value: priceListState }] = useField<MeetingServicePriceListState>("priceList")
    const priceList = priceListState?.item ?? null

    const { mutateAsync: callDeadlineMessage } = useApiMutation(fetchDeadlineMessage, {
        errorDisplay: ErrorDisplay.NONE,
    })

    const checkAddedProduct = useCallback(
        async (deliveriesWithAddedProduct: DeliveryTimesField): Promise<void> => {
            if (isSpecialOrder || !priceList) return

            const params = buildDeadlineMessageParams({
                priceList,
                deliveries: deliveriesWithAddedProduct,
                dateTimeFactory,
                productFlow: DeadlineMessageProductFlow.ADD_PRODUCT,
            })
            if (!params.deliveryTime) return

            try {
                const { isConfirmed, message, displayType } = selectDeadlineMessage(await callDeadlineMessage(params))
                if (isConfirmed || !message) return

                if (displayType === DeadlineMessageDisplayType.MODAL) {
                    setDeadlineDialogMessage(message)
                    return
                }
                if (displayType === DeadlineMessageDisplayType.SNACKBAR && message.displayRule) {
                    showDeadlineSnackbar(message.displayRule, "warning")
                }
                // CARD is shown by DeadlineMessageCard
            } catch {
                // fails open: the product is already in the cart
            }
        },
        [callDeadlineMessage, dateTimeFactory, isSpecialOrder, priceList, showDeadlineSnackbar],
    )

    const closeDeadlineDialog = useCallback(() => setDeadlineDialogMessage(null), [])

    return { checkAddedProduct, deadlineDialogMessage, closeDeadlineDialog }
}
